// Page entry. PRD 10 §1.2 / §7:
//   - CONTENT_LIB:NO_PERMISSION → LNB still shows the menu (PRD 21 §4.5), entry
//     is blocked here with the 권한 없음 modal.
//   - READ → sees the page (read-only); write controls are hidden.
//   - WRITE / Root → see [Manage Categories] + [Add Content]; Root additionally
//     gets delete (gated at the row/panel level in later slices).

import { useTranslation } from 'react-i18next';
import { FolderCog, Plus } from 'lucide-react';

import { useRequirePermission } from '@/shared/hooks/useRequirePermission';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';
import { HealthEncyclopediaView } from '@/features/health-encyclopedia/components/HealthEncyclopediaView';
import { useHealthEncyclopediaPageStore } from '@/features/health-encyclopedia/store/health-encyclopedia-page.store';

export function HealthEncyclopediaPage() {
  const { t } = useTranslation('health-encyclopedia');
  // SHCT-171 — was a private in-page card. PRD 21 §7.2 specifies 권한 없음 as a 모달 with
  // "접근 권한이 없습니다.", so every gated page now blocks the same way through this hook.
  const canRead = useRequirePermission('CONTENT_LIB', 'READ');
  // SHCT-582 — PRD 21 §7.4 P-2: a Role without WRITE sees the actions `disabled` + 툴팁, not a
  // header with nothing in it. Hiding them was the reported defect: the operator cannot tell a
  // missing permission from a missing feature.
  const { canWrite, denyProps } = useWriteGuard('CONTENT_LIB');
  const openCreate = useHealthEncyclopediaPageStore((s) => s.openCreate);
  const openCategoryManager = useHealthEncyclopediaPageStore((s) => s.openCategoryManager);

  if (!canRead) return null;

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('title')}</h1>
        </div>
        <div className="content__actions" style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={openCategoryManager}
            disabled={!canWrite}
            {...denyProps}
          >
            <FolderCog size={16} />
            {t('manageCategoryButton')}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={openCreate}
            disabled={!canWrite}
            {...denyProps}
          >
            <Plus size={16} />
            {t('addButton')}
          </button>
        </div>
      </div>
      <div className="content__body">
        <HealthEncyclopediaView />
      </div>
    </>
  );
}
